import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchParams, performSearch, setPreferences } from '../../store/searchSlice';
import { Select, MenuItem } from '@mui/material';
import TravelDatePicker from './DatePicker';
import DestinationInput from './DestinationInput';
import AirportInput from './AirportInput';
import './SearchForm.css';

// Custom hook for mobile detection and responsive behavior
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1200);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

function SearchForm({ onSearch }) {
  // Hooks for routing and state management
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchParams = useSelector((state) => state.search);
  
  // Local state management
  const [isSearching, setIsSearching] = useState(false);
  const isMobile = useIsMobile();
  const [error, setError] = useState(null);

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSearching(true);
    setError(null);

    try {
      // Reset pagination to page 1 before search
      dispatch(setPreferences({ currentPage: 1 }));
      
      // Dispatch search action with current parameters
      await dispatch(performSearch({
        searchParams: {
          destination: searchParams.destination,
          departureAirport: searchParams.departureAirport,
          date: searchParams.date,
          duration: searchParams.duration
        }
      }));

      // Navigate to results page
      navigate('/results');
      
      // Scroll handling for results view
      requestAnimationFrame(() => {
        const resultsContainer = document.querySelector('.results-container');
        if (resultsContainer) {
          resultsContainer.scrollTo({ 
            top: 0, 
            behavior: 'smooth' 
          });
        }
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });

      // Only reset searching state on desktop
      if (!isMobile) {
        setIsSearching(false);
      }
    } catch (error) {
      setError('Failed to search holidays. Please try again.');
      console.error('Search error:', error);
      setIsSearching(false);
    }
  };

  // Handler for mobile "Modify Search" button
  const handleModifySearch = () => {
    setIsSearching(false);
  };

  // Handler for date selection changes
  const handleDateChange = (dateString) => {
    dispatch(setSearchParams({ date: dateString }));
  };

  // Available duration options
  const durations = [4, 7, 10, 14];

  // Validate form completion status
  const isFormValid = () => {
    const hasDestination = searchParams.destination && 
      searchParams.destination.destinationTuple && 
      searchParams.destination.destinationTuple.some(value => value !== '');
    
    const hasAirport = Array.isArray(searchParams.departureAirport) 
      ? searchParams.departureAirport.length > 0  // Check array of airports
      : searchParams.departureAirport?.code;      // Check single airport

    return hasDestination && hasAirport;
  };

  // Show error state if search failed
  if (error) {
    return (
      <div className="error-message" style={{ color: 'red', padding: '1rem' }}>
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="search-form-container">
        <form 
          onSubmit={handleSubmit} 
          className={`search-form ${isMobile && isSearching ? 'searching' : ''}`}
        >
          {/* Destination field */}
          <div className="search-form-field destination-field">
            <label className="search-form-label">Destination</label>
            <DestinationInput
              value={searchParams.destination}
              onChange={(newValue) => dispatch(setSearchParams({ destination: newValue }))}
              required
            />
          </div>
          
          {/* Departure airport field */}
          <div className="search-form-field departure-field">
            <label className="search-form-label">Departure airport</label>
            <AirportInput
              value={searchParams.departureAirport}
              onChange={(newValue) => dispatch(setSearchParams({ departureAirport: newValue }))}
              required
            />
          </div>
          
          {/* Date selection field */}
          <div className="search-form-field date-field">
            <label className="search-form-label">When</label>
            <TravelDatePicker 
              value={searchParams.date}
              onChange={handleDateChange}
            />
          </div>
          
          {/* Duration selection field */}
          <div className="search-form-field duration-field">
            <label className="search-form-label">How long</label>
            <Select
              fullWidth
              value={searchParams.duration || 7}
              onChange={(e) => dispatch(setSearchParams({ duration: e.target.value }))}
              startAdornment={<span className="material-icons">nights_stay</span>}
            >
              {durations.map(duration => (
                <MenuItem key={duration} value={duration}>
                  {duration} nights
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Search button */}
          <button 
            type="submit" 
            className="search-button"
            disabled={isSearching || !isFormValid()}
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </form>
        
        {/* Mobile-only modify search button */}
        {isMobile && isSearching && (
          <div 
            className="modify-search-button"
            onClick={handleModifySearch}
          >
            Modify Search
          </div>
        )}
      </div>
    </>
  );
}

export default SearchForm;